import React from 'react';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { MorphologyClue, MorphologyData } from '../../../utils/types';
import {
  clearArchiveMorphologyState,
  loadArchiveMorphologyState,
  saveArchiveMorphologyState,
} from '../../../utils/persistenceUtils';
import { SpecialKeys } from '../shared/utils';
import { ArchiveMorphologyStore } from './ArchiveMorphologyStore';

// Records a completed Morphology puzzle in the user's archive list.
// For a logged-in user, finishing a puzzle (solved OR fully revealed) records the
// puzzleId in puzzleHistory.morphology.pastResults.completedArchives and NOTHING
// else. For a logged-out user, it does literally nothing.
// Exported because the daily Morphology puzzle also calls this on completion, so a
// signed-in user's daily solve is captured in the same archive list.
export const saveArchiveCompletion = async (puzzleId: number) => {
  const authToken = await getAuth().currentUser?.getIdToken(true);
  if (!authToken) return; // not logged in -> do nothing, on purpose

  try {
    await axios.post('/api/users/complete-archive', { authToken, puzzleId });
  } catch (error) {
    // best-effort; recording an archive completion is non-critical
  }
};

const onComplete = () => {
  const { puzzleId } = ArchiveMorphologyStore.getRawState();
  // Note: unlike the daily puzzle, we deliberately do NOT open the stats dialog.
  // The board just shows its completed/solved state.
  // Record the completion for signed-in users (no-op otherwise)...
  saveArchiveCompletion(puzzleId);
  // ...and drop the locally-saved in-progress state for this puzzle, since it's done.
  clearArchiveMorphologyState(puzzleId);
};

// Archive puzzles never touch streaks/stats. But unlike the daily flow, they DO
// persist in-progress state locally (for signed-in and signed-out users alike),
// keyed by the puzzle's id, so it can be resumed on reload. Called on pause,
// navigate-away, and after check/reveal (via setTimeout(saveMorphology, 100)).
export const saveMorphology = () => {
  const { puzzleId, time, solved, revealed, completed, guesses, revealedCells, mistakes }
    = ArchiveMorphologyStore.getRawState();

  // Once completed, the state is cleared by onComplete; nothing to persist.
  if (completed) return;

  saveArchiveMorphologyState({
    puzzleId,
    time,
    solved,
    revealed,
    completed,
    guesses,
    revealedCells,
    mistakes,
  });
};

// The archive endpoint returns the same shape as the daily puzzle plus an ISO date.
export type ArchiveMorphologyData = MorphologyData & { date: string };

// Format an ISO date string (YYYY-MM-DD) as e.g. "Friday, October 3, 2025".
// We parse the parts manually and build a UTC date, then format in UTC, so the
// displayed day never shifts due to the viewer's timezone.
export const formatArchiveDateLabel = (isoDate: string): string => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) return '';
  const [, year, month, day] = match;
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

export const initArchiveMorphology = (puzzleData: ArchiveMorphologyData) => {
  // The archive store is a singleton reused across archive dates, so we reset all
  // transient state here (not just the board) to avoid leaking state from a
  // previously-viewed archive puzzle. Then we overlay any locally-saved progress
  // for THIS puzzle id.
  const saved = loadArchiveMorphologyState(puzzleData.puzzleId);

  ArchiveMorphologyStore.update(s => {
    s.puzzleId = puzzleData.puzzleId;
    s.dateLabel = formatArchiveDateLabel(puzzleData.date);
    s.author = puzzleData.author;
    s.cryptic = !!puzzleData.cryptic;
    // it should be all caps anyway, but just in case of mistake we make sure here.
    s.clues = puzzleData.clues.map(clue => ({
      ...clue,
      solution: clue.solution.toUpperCase(),
    }));

    // Fresh-board defaults.
    s.guesses = s.clues.map((clue: any) => Array(clue.solution.length).fill(null));
    s.revealedCells = s.clues.map((clue: any) => Array(clue.solution.length).fill(false));
    s.mistakes = s.clues.map((clue: any) => Array(clue.solution.length).fill(false));
    s.time = 0;
    s.solved = false;
    s.revealed = false;
    s.completed = false;
    s.isPaused = false;
    s.isSoftPaused = false;
    s.focusedClueIndex = 0;
    s.cursorIndex = 0;
    s.autocheckOn = false;

    // Overlay saved progress, if any. We guard each array against shape drift
    // (e.g. if the puzzle data ever changed) by only using saved arrays whose
    // outer length matches the current clue count.
    if (saved) {
      const lengthsMatch = (arr?: unknown[]) => Array.isArray(arr) && arr.length === s.clues.length;
      if (lengthsMatch(saved.guesses)) s.guesses = saved.guesses;
      if (lengthsMatch(saved.revealedCells)) s.revealedCells = saved.revealedCells;
      if (lengthsMatch(saved.mistakes)) s.mistakes = saved.mistakes;
      s.time = saved.time || 0;
      s.solved = saved.solved || false;
      s.revealed = saved.revealed || false;
      s.completed = saved.completed || false;
    }
  });
};

let hasAlreadyShownAlmostThereDialog = false;
const shouldShowAlmostThereDialog = (guesses: (string | null)[][], revealedCells: boolean[][], clues: MorphologyClue[]) => {
  if (hasAlreadyShownAlmostThereDialog) return false;

  for (let i = 0; i < clues.length; i++) {
    const guess = guesses[i];

    for (let j = 0; j < guess.length; j++) {
      if (revealedCells[i][j]) continue;
      if (guess[j] === null) {
        return false;
      }
    }
  }

  hasAlreadyShownAlmostThereDialog = true;
  return true;
};

const isCompleted = (guesses: (string | null)[][], revealedCells: boolean[][], clues: MorphologyClue[]) => {
  for (let i = 0; i < clues.length; i++) {
    const solution = clues[i].solution;
    const guess = guesses[i];

    for (let j = 0; j < solution.length; j++) {
      if (revealedCells[i][j]) continue;
      if (guess[j] !== solution[j]) {
        return false;
      }
    }
  }

  return true;
};

// s = state, because this is meant to be used on a state draft inside a store update
export const moveCursorToFirstOpenSlot = (s: any) => {
  const guessForClue = s.guesses[s.focusedClueIndex];
  const nullIndex = guessForClue.findIndex(
    (letterGuess: string | null, i: number) => (letterGuess === null)
      && (s.revealedCells[s.focusedClueIndex][i] === false)
  );
  s.cursorIndex = Math.max(nullIndex, 0);
};

// s = state, because this is meant to be used on a state draft inside a store update
const moveCursorToNextOpenSlot = (s: any) => {
  const guessForClue = s.guesses[s.focusedClueIndex];
  // Find the first null index AFTER s.cursorIndex that isn't revealed
  let nullIndex = guessForClue.slice(s.cursorIndex + 1)
    .findIndex((guessForLetter: string | null, i: number) => 
      (guessForLetter === null) && s.revealedCells[s.focusedClueIndex][s.cursorIndex + 1 + i] === false
    ) + s.cursorIndex + 1;

  // if we didn't find one, then look for the first null index that isn't revealed, from the start
  if (nullIndex === -1) {
    nullIndex = guessForClue.findIndex((guessForLetter: string | null, i: number) =>
      (guessForLetter === null) && s.revealedCells[s.focusedClueIndex][i] === false);
  }

  if (nullIndex !== -1) {
    s.cursorIndex = nullIndex;
  } else {
    // If no null is found at all, move index forward by one unless we're at the end
    s.cursorIndex = s.cursorIndex === guessForClue.length - 1 ? s.cursorIndex : s.cursorIndex + 1;
  }
};

export const handleInput = (key: string, shiftKey?: boolean) => {
  const { completed, guesses, revealedCells, cursorIndex, focusedClueIndex }
    = ArchiveMorphologyStore.getRawState();

  if (completed) return;

  if (/^[a-zA-Z]$/.test(key)) {
    // solutions are always normalized to all caps
    key = key.toUpperCase();
    if (revealedCells[focusedClueIndex][cursorIndex]) {
      ArchiveMorphologyStore.update(s => {
        s.cursorIndex = Math.min(s.guesses[focusedClueIndex].length - 1, s.cursorIndex + 1);
      });
    } else {
      ArchiveMorphologyStore.update(s => {
        s.guesses[focusedClueIndex][cursorIndex] = key;
        s.mistakes[focusedClueIndex][cursorIndex] = false;
        s.cursorIndex = Math.min(s.guesses[focusedClueIndex].length - 1, s.cursorIndex + 1);
        if (s.revealedCells[s.focusedClueIndex][s.cursorIndex]) {
          moveCursorToNextOpenSlot(s);
        }
        if (isCompleted(s.guesses, s.revealedCells, s.clues)) {
          s.completed = true;
          if (!s.revealed) s.solved = true;
          onComplete();
        } else if (shouldShowAlmostThereDialog(s.guesses, s.revealedCells, s.clues)) {
          s.toolbar.almostThereIsOpen = true;
        }
      });
    }
  } else if (key === SpecialKeys.Space) {
    if (shiftKey) {
      clearWord();
      return;
    }
    // if revealed or blank, go to next slot unless that's revealed too. stop at end ofc.
    // (this is basically a forward backspace, i.e. Del on windows.)
    if (revealedCells[focusedClueIndex][cursorIndex] || !guesses[focusedClueIndex][cursorIndex]) {
      ArchiveMorphologyStore.update(s => {
        s.cursorIndex = Math.min(s.cursorIndex + 1, s.guesses[s.focusedClueIndex].length - 1);
      });
    } else {
      ArchiveMorphologyStore.update(s => {
        if (s.guesses[focusedClueIndex][cursorIndex]) {
          s.guesses[focusedClueIndex][cursorIndex] = null;
          s.mistakes[focusedClueIndex][cursorIndex] = false;
        }
      });
    }
  } else if (key === SpecialKeys.Delete) {
    // if revealed or blank, clear previous slot unless that's revealed too. stop at index 0 ofc.
    if (revealedCells[focusedClueIndex][cursorIndex] || !guesses[focusedClueIndex][cursorIndex]) {
      ArchiveMorphologyStore.update(s => {
        s.cursorIndex = Math.max(s.cursorIndex - 1, 0);
        if (!revealedCells[focusedClueIndex][s.cursorIndex]) {
          s.guesses[focusedClueIndex][s.cursorIndex] = null;
          s.mistakes[focusedClueIndex][s.cursorIndex] = false;
        }
      });
    } else {
      ArchiveMorphologyStore.update(s => {
        if (s.guesses[focusedClueIndex][cursorIndex]) {
          s.guesses[focusedClueIndex][cursorIndex] = null;
          s.mistakes[focusedClueIndex][cursorIndex] = false;
        }
      });
    }
  } else if (key === SpecialKeys.ArrowUp) {
    ArchiveMorphologyStore.update(s => {
      s.focusedClueIndex = Math.max(s.focusedClueIndex - 1, 0);
      s.cursorIndex = Math.min(Math.max(s.cursorIndex, 0), s.guesses[s.focusedClueIndex].length - 1);
    });
  } else if (key === SpecialKeys.ArrowDown) {
    ArchiveMorphologyStore.update(s => {
      s.focusedClueIndex = Math.min(s.focusedClueIndex + 1, s.clues.length - 1);
      s.cursorIndex = Math.min(Math.max(s.cursorIndex, 0), s.guesses[s.focusedClueIndex].length - 1);
    });
  } else if (key === SpecialKeys.ArrowLeft) {
    ArchiveMorphologyStore.update(s => {
      s.cursorIndex = Math.max(s.cursorIndex - 1, 0);
    });
  } else if (key === SpecialKeys.ArrowRight) {
    ArchiveMorphologyStore.update(s => {
      const lengthOfFocusedClue = s.clues[s.focusedClueIndex].solution.length;
      s.cursorIndex = Math.min(s.cursorIndex + 1, lengthOfFocusedClue - 1);
    });
  } else if (key === SpecialKeys.Tab) {
    if (shiftKey) {
      ArchiveMorphologyStore.update(s => {
        s.focusedClueIndex = focusedClueIndex - 1 < 0 ? guesses.length - 1 : focusedClueIndex - 1;
        moveCursorToFirstOpenSlot(s);
      });
    } else {
      ArchiveMorphologyStore.update(s => {
        s.focusedClueIndex = focusedClueIndex + 1 > guesses.length - 1 ? 0 : focusedClueIndex + 1;
        moveCursorToFirstOpenSlot(s);
      });
    }
  }
};

export const clearWord = () => {
  const { focusedClueIndex } = ArchiveMorphologyStore.getRawState();
  ArchiveMorphologyStore.update(s => {
    for (let n=0; n < s.guesses[focusedClueIndex].length; n++) {
      s.guesses[focusedClueIndex][n] = null;
      s.mistakes[focusedClueIndex][n] = false;
    }
    s.cursorIndex = 0;
  });
};

export const clearPuzzle = () => {
  if (ArchiveMorphologyStore.getRawState().solved) return;

  ArchiveMorphologyStore.update(s => {
    for (let n=0; n < s.guesses.length; n++) {
      for (let i=0; i < s.guesses[n].length; i++) {
        s.guesses[n][i] = null;
        s.mistakes[n][i] = false;
      }
      s.cursorIndex = 0;
      s.focusedClueIndex = 0;
    }
  });
};

export const revealLetter = () => {
  const { focusedClueIndex, cursorIndex, } = ArchiveMorphologyStore.getRawState();
  ArchiveMorphologyStore.update(s => {
    s.revealed = true;
    s.revealedCells[focusedClueIndex][cursorIndex] = true;
    s.mistakes[focusedClueIndex][cursorIndex] = false;
    if (isCompleted(s.guesses, s.revealedCells, s.clues)) {
      s.completed = true;
      setTimeout(onComplete, 100);
    } else {
      // let store update then save
      setTimeout(saveMorphology, 100);
    }
  });
};

export const revealWord = () => {
  const { focusedClueIndex } = ArchiveMorphologyStore.getRawState();
  ArchiveMorphologyStore.update(s => {
    s.revealed = true;
    for (let n=0; n < s.revealedCells[focusedClueIndex].length; n++) {
      s.revealedCells[focusedClueIndex][n] = true;
      s.mistakes[focusedClueIndex][n] = false;
    }
    if (isCompleted(s.guesses, s.revealedCells, s.clues)) {
      s.completed = true;
      setTimeout(onComplete, 100);
    } else {
      // let store update then save
      setTimeout(saveMorphology, 100);
    }
  });
};

export const revealPuzzle = () => {
  ArchiveMorphologyStore.update(s => {
    s.revealed = true;
    s.completed = true;

    for (let n=0; n < s.revealedCells.length; n++) {
      for (let i=0; i < s.revealedCells[n].length; i++) {
        s.revealedCells[n][i] = true;
      }
    }
  });

  // let store update then save
  setTimeout(onComplete, 100);
};

export const checkLetter = () => {
  const { autocheckOn, guesses, cursorIndex, focusedClueIndex, clues }
    = ArchiveMorphologyStore.getRawState();

  if (autocheckOn) return;

  if (guesses[focusedClueIndex][cursorIndex] === clues[focusedClueIndex].solution[cursorIndex]) {
    ArchiveMorphologyStore.update(s => {
      s.revealed = true;
      s.revealedCells[focusedClueIndex][cursorIndex] = true;
    });
  } else if (guesses[focusedClueIndex][cursorIndex] !== null) {
    ArchiveMorphologyStore.update(s => {
      s.revealed = true;
      s.mistakes[focusedClueIndex][cursorIndex] = true;
    });
  }

  // let store update then save
  setTimeout(saveMorphology, 100);
};

export const checkWord = () => {
  const { autocheckOn, guesses, focusedClueIndex, clues } = ArchiveMorphologyStore.getRawState();
  if (autocheckOn) return;

  ArchiveMorphologyStore.update(s => {
    s.revealed = true;
    for (let n=0; n < guesses[focusedClueIndex].length; n++) {
      // ignore already revealed cells
      if (!s.revealedCells[focusedClueIndex][n]) {
        // check if is right or not and mark it.
        if (guesses[focusedClueIndex][n] === clues[focusedClueIndex].solution[n]) {
          s.revealedCells[focusedClueIndex][n] = true;
        } else if (guesses[focusedClueIndex][n] !== null) {
          s.mistakes[focusedClueIndex][n] = true;
        }
      }
    }
  });

  // let store update then save
  setTimeout(saveMorphology, 100);
};

export const checkPuzzle = () => {
  const { guesses, clues } = ArchiveMorphologyStore.getRawState();

  ArchiveMorphologyStore.update(s => {
    s.revealed = true;
    for (let i=0; i < guesses.length; i++) {
      for (let n=0; n < guesses[i].length; n++) {
        // ignore already revealed cells
        if (!s.revealedCells[i][n]) {
          // check if is right or not and mark it.
          if (guesses[i][n] === clues[i].solution[n]) {
            s.revealedCells[i][n] = true;
          } else if (guesses[i][n] !== null) {
            s.mistakes[i][n] = true;
          }
        }
      }
    }
  });

  // let store update then save
  setTimeout(saveMorphology, 100);
};

export const toggleAutoCheck = () => {
  ArchiveMorphologyStore.update(s => {
    s.revealed = true;
    s.autocheckOn = !s.autocheckOn;
  });
};

export enum ConfirmationTypes {
  CheckLetter = 'CheckLetter',
  CheckWord = 'CheckWord',
  CheckPuzzle = 'CheckPuzzle',
  Autocheck = 'Autocheck',
  RevealLetter = 'RevealLetter',
  RevealWord = 'RevealWord',
  RevealPuzzle = 'RevealPuzzle',
};

// RevealConfirmationDialogCluster contains a different confirmation dialog for each
// of these, because in each case the confirm has to do something different.
// Arguably we should replace it with one dialog and just change the onConfirm prop that's passed
// Right now we are toggling 7 different isOpens to show/hide the dialog matches
// the option the user has selected. But we can change this later.
export const maybeConfirmAndProceed = (confirmationType: ConfirmationTypes) => {
  const { revealed, completed } = ArchiveMorphologyStore.getRawState();
  if (completed) return;

  switch (confirmationType) {
    case ConfirmationTypes.CheckLetter: {
      if (revealed) {
        checkLetter();
      } else {
        ArchiveMorphologyStore.update(s => {
          s.toolbar.confirmationDialog.checkLetterIsOpen = true;
          s.isSoftPaused = true;
        });
      }
      break;
    }
    case ConfirmationTypes.CheckWord: {
      if (revealed) {
        checkWord();
      } else {
        ArchiveMorphologyStore.update(s => {
          s.toolbar.confirmationDialog.checkWordIsOpen = true;
          s.isSoftPaused = true;
        });
      }
      break;
    }
    case ConfirmationTypes.CheckPuzzle: {
      if (revealed) {
        checkPuzzle();
      } else {
        ArchiveMorphologyStore.update(s => {
          s.toolbar.confirmationDialog.checkPuzzleIsOpen = true;
          s.isSoftPaused = true;
        });
      }
      break;
    }
    case ConfirmationTypes.Autocheck: {
      if (revealed) {
        toggleAutoCheck();
      } else {
        ArchiveMorphologyStore.update(s => {
          s.toolbar.confirmationDialog.autocheckIsOpen = true;
          s.isSoftPaused = true;
        });
      }
      break;
    }
    case ConfirmationTypes.RevealLetter: {
      if (revealed) {
        revealLetter();
      } else {
        ArchiveMorphologyStore.update(s => {
          s.toolbar.confirmationDialog.revealLetterIsOpen = true;
          s.isSoftPaused = true;
        });
      }
      break;
    }
    case ConfirmationTypes.RevealWord: {
      if (revealed) {
        revealWord();
      } else {
        ArchiveMorphologyStore.update(s => {
          s.toolbar.confirmationDialog.revealWordIsOpen = true;
          s.isSoftPaused = true;
        });
      }
      break;
    }
    case ConfirmationTypes.RevealPuzzle: {
      // always confirm this, even if it's already marked Revealed
      ArchiveMorphologyStore.update(s => {
        s.toolbar.confirmationDialog.revealAllIsOpen = true;
        s.isSoftPaused = true;
      });
      break;
    }
  }
};

export const updateScrollPositions = (focusedClueIndex: number) => {
  const clues = ArchiveMorphologyStore.getRawState().clues;

  const clueRows = document.querySelectorAll('.clueRow');
  const cluesContainer = document.querySelector('.cluesContainer')!;

  const focused = clueRows[focusedClueIndex] as HTMLElement;
  const focusedRect = focused.getBoundingClientRect();
  const cluesContainerRect = cluesContainer.getBoundingClientRect();

  const focusedIsBelowView = focusedRect.top > cluesContainerRect.bottom - 60;
  const focusedIsAboveView = focusedRect.bottom < cluesContainerRect.top + 40;

  if (focusedIsBelowView) {
    if (focusedClueIndex === clues.length - 1) {
      cluesContainer.scrollTop = cluesContainer.scrollHeight;
    } else {
      const newScroll = cluesContainer.scrollTop + focusedRect.height;
      if (focusedRect.top > cluesContainerRect.bottom) {
        cluesContainer.scrollTop =
          focused.offsetTop + focused.offsetHeight - cluesContainer.clientHeight + 40;
      } else {
        cluesContainer.scrollTop = newScroll;
      }
    }
    return;
  }

  if (focusedIsAboveView) {
    if (focusedClueIndex === 0) {
      cluesContainer.scrollTop = 0;
    } else {
      const newScroll = cluesContainer.scrollTop - focusedRect.height;
      if (focusedRect.bottom < cluesContainerRect.top) {
        cluesContainer.scrollTop = focused.offsetTop;
      } else {
        cluesContainer.scrollTop = newScroll;
      }
    }
    return;
  }
};

/**
 * Parses clue text containing italic markers (^text^) and returns JSX with
 * styled spans for italicized portions.
 * Example: "This is ^an^ example" becomes "This is <span className="italicText">an</span> example"
 */
export const parseClueWithItalics = (clueText: string): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  let currentIndex = 0;
  let keyCounter = 0;

  // Find all italic sections marked with ^text^
  const italicRegex = /\^([^^]+)\^/g;
  let match: RegExpExecArray | null;

  while ((match = italicRegex.exec(clueText)) !== null) {
    // Add text before the italic section
    if (match.index > currentIndex) {
      parts.push(clueText.substring(currentIndex, match.index));
    }

    // Add the italic section
    parts.push(
      React.createElement('span', { key: keyCounter++, className: 'italicText' }, match[1])
    );

    currentIndex = match.index + match[0].length;
  }

  // Add remaining text after last italic section
  if (currentIndex < clueText.length) {
    parts.push(clueText.substring(currentIndex));
  }

  // If no italic markers were found, just return the original text
  return parts.length === 0 ? clueText : parts;
};